/**
 * Timing Configuration
 *
 * Centralized timing constants for the application.
 * Replaces magic numbers scattered throughout the codebase.
 *
 * Categories:
 * - UI: User interface delays and animations
 * - Storage: IndexedDB and localStorage operations
 * - Events: Event system timing
 * - Chart: Chart rendering and initialization
 */

/**
 * UI Timing Constants
 */
export const UI_TIMING = {
  /** Input focus delay after render (ms) */
  INPUT_FOCUS_DELAY: 100,

  /** Confirmation button auto-reset timeout (ms) */
  CONFIRM_BUTTON_RESET: 3000,

  /** Accordion section focus delay (ms) */
  ACCORDION_FOCUS_DELAY: 150,

  /** Toast notification duration (ms) */
  TOAST_DURATION: 3000,

  /** Debounce for rapid user input (ms) */
  INPUT_DEBOUNCE: 300,

  /** Long press activation delay (ms) */
  LONG_PRESS_DELAY: 500,

  /** Hold-to-repeat initial delay (ms) */
  HOLD_REPEAT_INITIAL: 500,

  /** Hold-to-repeat interval (ms) */
  HOLD_REPEAT_INTERVAL: 100,
} as const;

/**
 * Storage Timing Constants
 */
export const STORAGE_TIMING = {
  /** Storage initialization poll interval (ms) */
  INIT_POLL_INTERVAL: 100,

  /** Recent files reload delay (ms) */
  RECENT_FILES_RELOAD: 100,

  /** Auto-save debounce (ms) */
  AUTO_SAVE_DEBOUNCE: 2000,

  /** Auto-save interval (ms) */
  AUTO_SAVE_INTERVAL: 30000,

  /** Session restore delay (ms) */
  SESSION_RESTORE_DELAY: 1000,

  /** Workspace restoration animation suppression duration (ms) */
  WORKSPACE_RESTORE_ANIMATION_DELAY: 5000,
} as const;

/**
 * Event System Timing Constants
 */
export const EVENT_TIMING = {
  /** Event deduplication window (ms) */
  EVENT_DEDUP_WINDOW: 100,

  /** Event throttle interval (ms) */
  EVENT_THROTTLE: 16, // ~60fps

  /** Event debounce default (ms) */
  EVENT_DEBOUNCE: 100,

  /** Memory leak detection interval (ms) */
  MEMORY_LEAK_CHECK_INTERVAL: 30000,
} as const;

/**
 * Chart Timing Constants
 */
export const CHART_TIMING = {
  /** Chart initialization retry delay (ms) */
  INIT_RETRY_DELAY: 100,

  /** Panchanga section initialization delay (ms) */
  PANCHANGA_INIT_DELAY: 200,

  /** Map auto-location trigger delay (ms) */
  MAP_LOCATION_DELAY: 100,

  /** DOM ready retry interval (ms) */
  DOM_READY_RETRY: 100,

  /** Chakra render completion buffer (ms) */
  CHAKRA_RENDER_BUFFER: 50,

  /** Performance measurement delay (ms) */
  PERF_MEASURE_DELAY: 0, // nextTick

  /** Chart switch animation duration (ms) */
  CHART_SWITCH_ANIMATION: 200,
} as const;

/**
 * Motion Buffer Timing Constants
 */
export const MOTION_TIMING = {
  /** Progressive loading poll interval (ms) */
  POLL_INTERVAL: 500,

  /** Maximum poll attempts before timeout (120 × 500ms = 60s) */
  MAX_POLL_ATTEMPTS: 120,
} as const;

/**
 * Animation Timing Constants
 */
export const ANIMATION_TIMING = {
  /** Fast animation (ms) */
  FAST: 150,

  /** Normal animation (ms) */
  NORMAL: 300,

  /** Slow animation (ms) */
  SLOW: 500,

  /** Planet sprite fade duration (ms) */
  PLANET_FADE: 200,

  /** Chakra show/hide duration (ms) */
  CHAKRA_TOGGLE: 250,
} as const;

/**
 * All timing constants combined
 */
export const TIMING = {
  UI: UI_TIMING,
  STORAGE: STORAGE_TIMING,
  EVENT: EVENT_TIMING,
  CHART: CHART_TIMING,
  MOTION: MOTION_TIMING,
  ANIMATION: ANIMATION_TIMING,
} as const;

export default TIMING;
